import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { InstrumentTypeResponse } from "../../../../codegen-api";
import { Button, ButtonThemeEnum } from "../../../../components/Buttons/styles";
import { PortfolioMarketFilter } from "../../../../components/PortfolioSettings/PortfolioMarketFilter";
import SyncStatusIndicator, {
  LOAD_FAILED_STATUS,
} from "../../../../components/TradingBottomBar/SyncStatusIndicator";
import { useGetAccount } from "../../../../hooks/api/account/useGetAccount";
import { Content } from "../../../../components/PortfolioSettings/style";
import { Spinner } from "../../../../components/shared/Spinner";
import { SpinnerContainerWrapper } from "../../../../components/shared/Spinner/style";
import { useUrlInstrumentFilter } from "../../../../hooks/useUrlParamState";
import usePositionsWSS from "../../../../hooks/wss/usePositionsWSS";
import { MobileWrapper, PortfolioFilterContainer } from "../style";
import MobilePositions from "./MobilePositions";
import { IMobilePositionsTabProps } from "./types";

export function MobilePositionsTab({
  showFilter = true,
  noPadding = false,
}: IMobilePositionsTabProps) {
  const { loading, positions, error } = usePositionsWSS();
  const { mutate: retryAccount } = useGetAccount();
  const [filter, setFilter] = useUrlInstrumentFilter();
  const { t } = useTranslation("app", {
    keyPrefix: "pages.PortfolioPage.mobile.MobilePositionsTab",
  });

  const filteredPositions = useMemo(() => {
    if (filter === InstrumentTypeResponse.Option) {
      return positions.filter((p) => !!p.option);
    }
    if (filter === InstrumentTypeResponse.Perpetual) {
      return positions.filter(
        (p) => p.instrument_type === InstrumentTypeResponse.Perpetual
      );
    }
    if (filter === InstrumentTypeResponse.Spot) {
      return positions.filter(
        (p) => p.instrument_type === InstrumentTypeResponse.Spot
      );
    }
    return positions;
  }, [filter, positions]);

  if (loading) {
    return (
      <SpinnerContainerWrapper>
        <Spinner />
      </SpinnerContainerWrapper>
    );
  }

  return (
    <MobileWrapper $noPadding={noPadding}>
      {showFilter ? (
        <PortfolioFilterContainer>
          <PortfolioMarketFilter
            title={t("positions")}
            instrumentType={filter}
            onSetFilter={setFilter}
          />
        </PortfolioFilterContainer>
      ) : null}
      {error && positions.length > 0 ? (
        // Older rows are still shown, but the read that should confirm them failed.
        <SyncStatusIndicator
          status={LOAD_FAILED_STATUS}
          onRetry={() => void retryAccount()}
          t={t}
        />
      ) : null}
      {/* eslint-disable-next-line no-nested-ternary */}
      {!loading && filteredPositions.length < 1 ? (
        error && positions.length === 0 ? (
          // Unknown, not empty: `/account` failed, so "You have no positions"
          // would be a claim.
          <Content role="alert">
            {t("positions_load_failed", {
              defaultValue: "Couldn't load your positions.",
            })}{" "}
            <Button
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={() => void retryAccount()}
            >
              {t("retry", { defaultValue: "Retry" })}
            </Button>
          </Content>
        ) : (
          <Content>{t("no_history_desc")}</Content>
        )
      ) : (
        <MobilePositions positions={filteredPositions} t={t} />
      )}
    </MobileWrapper>
  );
}
